import {Dictionary, convertAllErrorsFromBeToFormError, putAll, takeLatestF, validateSchema} from '@fl/cmsch-fe-library';
import {isUndefined, keys, omitBy} from 'lodash/fp';
import {SagaIterator} from 'redux-saga';
import {isEmpty, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {ValidateCreateOrganizationBindingAdmin} from 'api/gen/ValidateCreateOrganizationBindingAdmin';
import {ValidateOrganizationBindingManager} from 'api/gen/ValidateOrganizationBindingManager';
import {
    buildSubjectManagementCenterOptions,
    BuildSubjectManagementCenters,
} from 'app/admin/model/sagas/build-subject-management-center-options';
import {t} from 'app/translations';
import {ValidateOrganizationErrorsSchema} from 'app/user-detail/types/errors/validate-organization-errors';
import {formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {
    managementOrganizationFormName,
    ManagementOrganizationFormTag,
    ManagementOrganizationFormValuesErrors,
} from '../../types/management-organization-form-values';
import {ValidateManagementOrganizationAction} from '../action';

interface ApiTypes {
    organizationId: number | null;
    roleIds: Array<number> | null;
    userProfileId: number;
    approvedFrom: string;
    approvedTo: string | null;
    tag: ManagementOrganizationFormTag;
    farmIds: Array<number> | null;
    organizationCenterIds: Array<number> | null;
    userProfileOrganizationRoleId: number | null;
}
// eslint-disable-next-line max-lines-per-function
const getApiValues = ({
    organizationId,
    roleIds,
    userProfileId,
    approvedFrom,
    approvedTo,
    userProfileOrganizationRoleId,
    farmIds,
    organizationCenterIds,
    tag,
}: ApiTypes): ValidateOrganizationBindingManager => tag === 'ValidateCreateOrganizationBindingManager'
    ? ({
        tag,
        binding: {
            approvedFrom,
            approvedTo,
            organizationId: opt(organizationId).orCrash('No organizationId found'),
            roleIds: opt(roleIds).orCrash('No roleIds found'),
            userProfileId,
            farmIds,
            organizationCenterIds,
        },
    })
    : {
        tag,
        binding: {
            approvedTo,
            userProfileOrganizationRoleId: opt(userProfileOrganizationRoleId)
                .orCrash('No userProfileOrganizationRoleId found'),
            farmIds,
            organizationCenterIds,
        },
    };

const mapErrors = (
    errors: Dictionary<keyof ValidateCreateOrganizationBindingAdmin, string>,
): ManagementOrganizationFormValuesErrors => {
    const validErrors = validateSchema(ValidateOrganizationErrorsSchema, 'FE')(errors).binding;

    return omitBy(isUndefined)<ManagementOrganizationFormValuesErrors>({
        approvedFrom: validErrors.approvedFrom,
        approvedTo: validErrors.approvedTo,
        organizationId: validErrors.organizationId,
        userProfileId: validErrors.organizationId,
        roleIds: validErrors.roleIds,
        farmIds: validErrors.farmIds,
        organizationCenterIds: validErrors.organizationCenterIds,
    });
};

// eslint-disable-next-line max-lines-per-function
function* execute(_: ValidateManagementOrganizationAction): SagaIterator {
    const {
        approvedFrom,
        approvedTo,
        roleIds,
        userProfileId,
        farmIds,
        organizationCenterIds,
        organizationId,
        userProfileOrganizationRoleId,
        tag,
    } = (
        yield* select(formHelpers.formValues(managementOrganizationFormName))
    ).orCrash(`no ${managementOrganizationFormName} form values`);

    const pristine = yield* select(formHelpers.isPristine(managementOrganizationFormName));
    if (
        approvedFrom === null
        || userProfileId === null
        || isEmpty(roleIds)
        || isEmpty(farmIds) && isEmpty(organizationCenterIds)
        || pristine
        || (approvedTo !== null && approvedFrom > approvedTo)
    ) return; // synchronously not valid

    const buildFarms: BuildSubjectManagementCenters = {centerIds: farmIds, type: 'MANAGEMENT_ORGANIZATION_FARM'};
    const computedFarmIds = yield* call(buildSubjectManagementCenterOptions, buildFarms);

    const buildCenters: BuildSubjectManagementCenters = {
        centerIds: organizationCenterIds,
        type: 'MANAGEMENT_ORGANIZATION_CENTER',
    };
    const computedCenterIds = yield* call(buildSubjectManagementCenterOptions, buildCenters);

    yield* put(formHelpers.startAsyncValidation(managementOrganizationFormName));

    const response = yield* call(
        Api.validateUserProfileOrganizationRoleBySubjectManager,
        getApiValues({
            organizationId,
            roleIds,
            userProfileId,
            approvedFrom,
            approvedTo,
            tag: opt(tag).orCrash('Tag does not exist'),
            userProfileOrganizationRoleId,
            farmIds: computedFarmIds,
            organizationCenterIds: computedCenterIds,
        }),
    );

    if (!response.isSuccess) {
        if (response.isBadRequest) {
            const errors = convertAllErrorsFromBeToFormError(response.data);
            const organizationErrors = mapErrors(errors.errors);
            yield* put(formHelpers.stopAsyncValidation(managementOrganizationFormName, organizationErrors));
            const fieldsWithNewErrors = keys(organizationErrors) as Array<keyof typeof organizationErrors>;

            if (!isEmpty(fieldsWithNewErrors)) {
                yield* put(formHelpers.touch(managementOrganizationFormName, ...fieldsWithNewErrors));
            }
        } else {
            yield putAll(showBeError(response, t('common')('validateEntry')));
        }
    } else {
        yield* put(formHelpers.stopAsyncValidation(managementOrganizationFormName, {}));
    }
}

/** Runs the admin saga that validates organization-management form values before save. */
export function* validateManagementOrganizationSaga(): SagaIterator {
    yield takeLatestF('admin/VALIDATE_MANAGEMENT_ORGANIZATION', execute);
}
